import { createSignal, createResource, For, Show, lazy, Suspense, createEffect, onCleanup } from "solid-js";
import { TabBar, type TabId } from "./components/TabBar";
import { SlotsTab } from "./tabs/SlotsTab";
import { BuyCreditsModal } from "./components/BuyCreditsModal";
import { SpriteInline } from "./components/SpriteInline";
import { TelegramLoginGate } from "./components/TelegramLoginGate";
import { api, type ProfileDTO } from "./lib/api";
import { setLanguage, lang as i18nLang, t } from "./lib/i18n";
import { isDemoMode } from "./lib/demo";
import { isInTelegram } from "./lib/auth";

// Lazy-load secondary tabs, keeps initial bundle lean for first paint.
const ProfileTab = lazy(() => import("./tabs/ProfileTab").then((m) => ({ default: m.ProfileTab })));
const SavedTab = lazy(() => import("./tabs/SavedTab").then((m) => ({ default: m.SavedTab })));

export default function App() {
  const [tab, setTab] = createSignal<TabId>("slots");
  const demo = isDemoMode();
  // Tri-state auth gate for the browser path:
  //   null    = still trying /api/me
  //   false   = unauthenticated (show TelegramLoginGate)
  //   true    = signed in via cookie (full app)
  // Telegram Mini App + demo skip this entirely, they always have a
  // "user" (initData or the local demo store), so /api/me 401 means
  // genuine auth failure and we still flip to false.
  const [authed, setAuthed] = createSignal<boolean | null>(
    demo || isInTelegram() ? true : null,
  );
  const [botUsername, setBotUsername] = createSignal<string>("SaaSlotsBot");

  // In demo mode `api.me()` is wired to the localStorage demo-store so the
  // resource always resolves, the spin counter increments, saved ideas
  // persist, etc. In Telegram-WebApp mode it hits the worker as before.
  // createResource needs a truthy source signal to fire; we wrap the
  // tri-state authed() so the fetcher only runs once we know we're allowed.
  const [profile, { mutate: setProfile, refetch }] = createResource<
    ProfileDTO | null,
    true
  >(
    () => (authed() === false ? undefined : true),
    async () => {
      try {
        const p = await api.me();
        setLanguage(p.language);
        if (authed() !== true) setAuthed(true);
        return p;
      } catch (e: unknown) {
        // Browser path: 401 means "not signed in", show the gate. Other
        // errors leave us in the loading state so a transient network blip
        // doesn't kick the user out.
        const status = (e as { status?: number })?.status;
        if (status === 401 && !demo && !isInTelegram()) {
          setAuthed(false);
        }
        return null;
      }
    },
  );

  // Fetch the bot username once on first paint for the login widget. Cheap,
  // and saves hard-coding @SaaSlotsBot in the SPA bundle.
  createEffect(() => {
    if (authed() !== false) return;
    api.authConfig().then((cfg) => setBotUsername(cfg.bot_username)).catch(() => {
      /* fall back to default */
    });
  });

  // Keep <html lang="…"> in sync with the UI language so screen readers and
  // browser translation prompts get it right.
  createEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = i18nLang();
    }
  });

  /**
   * After each spin, bump the counter locally (optimistic, saves a /me round
   * trip that previously re-rendered the whole tree at the worst possible
   * moment). A debounced background refetch eventually reconciles with the
   * worker (real mode) or just reads back from localStorage (demo mode).
   */
  let reconcileTimer: ReturnType<typeof setTimeout> | undefined;
  onCleanup(() => {
    if (reconcileTimer) clearTimeout(reconcileTimer);
  });
  const handleSpinComplete = async (): Promise<void> => {
    // `api.recordSpin` is the single boundary: returns the new profile in
    // demo, null in prod. App code stays unaware of localStorage.
    const updated = await api.recordSpin();
    if (updated) {
      setProfile(updated);
      return;
    }
    const cur = profile();
    if (cur) {
      // Optimistic local debit: prefer free first, then paid, mirrors
      // the worker's `consumeSpin`. Reconcile in 3s.
      const free = cur.free_left > 0;
      setProfile({
        ...cur,
        total_spins: cur.total_spins + 1,
        free_left: free ? cur.free_left - 1 : cur.free_left,
        credits: free ? cur.credits : Math.max(0, cur.credits - 1),
      });
    }
    if (reconcileTimer) clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(() => refetch(), 3000);
  };

  // Buy-modal trigger is hoisted here so both Slots (auto-open on 0/0)
  // and Profile (manual "Buy spins" button) can drive it.
  const [buyOpen, setBuyOpen] = createSignal(false);
  const openBuy = () => setBuyOpen(true);
  const onPaid = () => {
    // Webhook needs a moment; retry a couple of times to catch the new
    // balance instead of leaving the user wondering if the payment stuck.
    let tries = 0;
    const poll = () => {
      refetch();
      tries += 1;
      if (tries < 4) setTimeout(poll, 1200);
    };
    poll();
  };

  return (
    // Outer page. On mobile invisible (full-screen child). On sm+ becomes
    // the "jewel-box" stage: warm radial backdrop, soft golden halo behind
    // the card, film grain, and drifting gold dust, the phone-card reads
    // as a museum exhibit under a single warm spotlight.
    <div class="stage min-h-dvh sm:flex sm:items-center sm:justify-center sm:p-6">
      <SpriteInline />
      <StageDust />
      <div
        id="phone-card"
        class="phone-card relative flex h-dvh w-full flex-col overflow-hidden bg-cream-100 sm:h-[844px] sm:max-h-[calc(100dvh-48px)] sm:w-[420px] sm:max-w-[420px] sm:rounded-[44px] sm:shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6),0_0_0_1px_rgba(184,146,63,0.15)]"
      >
        {/* The tiny `DEMO` chip sits at the phone-card corner, OUTSIDE the
            scroll container, so it stays in place on every tab and never
            scrolls away. It replaces the old full-width sticky ribbon
            which ate vertical space and stomped on the wordmark. */}
        <Show when={demo}>
          <DemoBadge />
        </Show>

        <Show when={authed() === false}>
          <TelegramLoginGate
            botUsername={botUsername()}
            onAuthed={() => {
              setAuthed(true);
              refetch();
            }}
          />
        </Show>

        {/* Inner scroll wrapper. Scrolling here keeps the tab bar pinned to
            the bottom of the phone-card regardless of scroll position,the
            old single-overflow layout let `position: absolute` children
            slide with the scrolled content (P0 on iPhone SE 320×568). */}
        <div
          id="phone-scroll"
          class="flex-1 overflow-y-auto overflow-x-hidden"
          style={{
            "overscroll-behavior": "contain",
            display: authed() === false ? "none" : undefined,
          }}
        >
          <Show when={tab() === "slots"}>
            <SlotsTab
              profile={profile() ?? null}
              onSpinComplete={handleSpinComplete}
              onSpinFailed={() => refetch()}
              onNeedCredits={openBuy}
            />
          </Show>
          <Show when={tab() === "profile"}>
            <Suspense fallback={<TabFallback />}>
              <ProfileTab
                profile={profile() ?? null}
                onProfileChange={(p) => setProfile(p)}
                onBuyCredits={openBuy}
              />
            </Suspense>
          </Show>
          <Show when={tab() === "saved"}>
            <Suspense fallback={<TabFallback />}>
              <SavedTab />
            </Suspense>
          </Show>
        </div>

        <Show when={authed() !== false}>
          <TabBar active={tab()} onChange={setTab} />
        </Show>
      </div>
      <BuyCreditsModal
        open={buyOpen()}
        onClose={() => setBuyOpen(false)}
        onPaid={onPaid}
      />
    </div>
  );
}

/**
 * Tiny `DEMO` chip pinned to the top-left of the phone-card. Click (or
 * hover on desktop) opens a popover that explains the demo restriction
 * and links to the Telegram bot. Outside-click and Escape close it.
 */
function DemoBadge() {
  const [open, setOpen] = createSignal(false);
  let wrapRef: HTMLDivElement | undefined;

  // Hover handling is only useful on devices with a real pointer; iOS will
  // fire `mouseenter` after a tap which would prevent the click toggle
  // from feeling right. Detect once at mount.
  const hasHover =
    typeof window !== "undefined" &&
    window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  // Close on outside click / Escape.
  createEffect(() => {
    if (!open()) return;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    onCleanup(() => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    });
  });

  return (
    <div
      ref={wrapRef}
      style={{
        position: "absolute",
        top: "calc(var(--app-safe-top) + 4px)",
        left: "14px",
        "z-index": 60,
      }}
      onMouseEnter={hasHover ? () => setOpen(true) : undefined}
      onMouseLeave={hasHover ? () => setOpen(false) : undefined}
    >
      <button
        type="button"
        class="demo-badge"
        onClick={() => setOpen((v) => !v)}
        aria-label={t("demo.badgeAria")}
        aria-expanded={open()}
      >
        <span class="demo-dot" aria-hidden="true" />
        <span>{t("demo.badge")}</span>
      </button>

      <Show when={open()}>
        <div role="tooltip" class="demo-popover">
          <div class="demo-popover-title">{t("demo.tooltipTitle")}</div>
          <div class="demo-popover-body">{t("demo.tooltipBody")}</div>
          <a
            class="demo-popover-cta"
            href="https://t.me/SaaSlotsBot"
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t("demo.openInTelegramAria")}
          >
            <span>{t("demo.openInTelegram")}</span>
            <span class="demo-popover-cta-arrow" aria-hidden="true">→</span>
          </a>
        </div>
      </Show>
    </div>
  );
}

/**
 * Drifting gold dust over the desktop stage. 22 GPU-composited particles
 * with randomised start position, sway, duration, and a negative delay so
 * the animation looks like it's been running forever (no "all rise at
 * once" tick on first paint). Hidden under 640 px via CSS, the dust is a
 * desktop-only flourish and would just eat battery on phones.
 */
function StageDust() {
  const particles = Array.from({ length: 22 }, () => {
    const dur = 18 + Math.random() * 18; // 18,36 s
    return {
      left: Math.random() * 100,
      size: 1 + Math.random() * 1.6, // 1.0,2.6 px
      dx: (Math.random() - 0.5) * 80, // horizontal sway in px
      dur,
      delay: -Math.random() * dur, // start mid-cycle
    };
  });
  return (
    <div class="stage-dust" aria-hidden="true">
      <For each={particles}>
        {(p) => (
          <i
            style={{
              left: `${p.left}%`,
              width: `${p.size}px`,
              height: `${p.size}px`,
              "animation-duration": `${p.dur}s`,
              "animation-delay": `${p.delay}s`,
              "--dx": `${p.dx}px`,
            }}
          />
        )}
      </For>
    </div>
  );
}

function TabFallback() {
  return (
    <div class="flex h-full items-center justify-center" style={{ "padding-top": "40px" }}>
      <div
        style={{
          width: "32px",
          height: "32px",
          "border-radius": "16px",
          background: "linear-gradient(135deg, #f4e5b8 0%, #c9a961 50%, #b8923f 100%)",
          opacity: 0.7,
          animation: "shimmer 2s linear infinite",
        }}
      />
    </div>
  );
}
